import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Typography, MenuItem, Select, FormControl, InputLabel, Box, RadioGroup, Radio, FormControlLabel, FormLabel, CircularProgress, InputAdornment, Tabs, Tab, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import COLORS from '../../../../../../constants/colors';
import AirtelLogo from '../../../../../../assets/Ourpartners/airtel.png';
import MtnLogo from '../../../../../../assets/Ourpartners/mtn.png';
import baseURL from '../../../../user/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faMoneyBillWave, faEnvelope, faPhone, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from "react-router-dom";
import { PayPalButtons, FUNDING, PayPalScriptProvider } from "@paypal/react-paypal-js";

// Fetch exchange rate from backend for consistent conversion
let cachedRate = null;
let rateFetchedAt = 0;
const RATE_CACHE_MS = 10 * 60 * 1000; // 10 minutes

const getExchangeRate = async () => {
    if (cachedRate && Date.now() - rateFetchedAt < RATE_CACHE_MS) {
        return cachedRate;
    }
    try {
        const res = await fetch(`${baseURL}/paypal/exchange-rate`);
        const data = await res.json();
        cachedRate = data;
        rateFetchedAt = Date.now();
        return data;
    } catch {
        // Fallback rate if backend is unreachable
        return { UGX_TO_USD: 3300, USD_TO_UGX: 3300, UGX_TO_USD_RATE: 1 / 3300 };
    }
};

const convertCurrency = async (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;

    const rates = await getExchangeRate();

    if (fromCurrency === 'UGX' && toCurrency === 'USD') {
        const converted = amount * rates.UGX_TO_USD_RATE;
        if (converted < 0.5 && amount > 0) {
            toast.warn("Converted USD amount is very small. Consider donating a larger UGX amount.");
        }
        return converted.toFixed(2);
    }

    if (fromCurrency === 'USD' && toCurrency === 'UGX') {
        return Math.round(amount * rates.USD_TO_UGX);
    }

    throw new Error(`Unsupported conversion: ${fromCurrency} to ${toCurrency}`);
};

// PayPal Button Wrapper — defined outside DonateDialog to prevent SDK reload on every render
const PayPalButtonWrapper = ({ paypalLoading, paypalClientId, isPayPalFormValid, createPayPalOrder, onPayPalApprove }) => {
    if (paypalLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading PayPal...</Typography>
            </Box>
        );
    }

    if (!paypalClientId) {
        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography color="error">
                    PayPal is currently unavailable. Please try another payment method.
                </Typography>
            </Box>
        );
    }

    return (
        <PayPalScriptProvider
            options={{
                clientId: paypalClientId,
                currency: "USD",
                intent: "capture"
            }}
        >
            <PayPalButtons
                fundingSource={FUNDING.PAYPAL}
                disabled={!isPayPalFormValid}
                style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "pay",
                }}
                createOrder={createPayPalOrder}
                onApprove={onPayPalApprove}
                onError={(error) => {
                    console.error("PayPal Button Error:", error);
                    toast.error("An error occurred with PayPal. Please try again.");
                }}
            />
        </PayPalScriptProvider>
    );
};

const DonateDialog = ({ open, onClose, campaign, onDonate }) => {
    const [donationAmount, setDonationAmount] = useState('');
    const [donationAmountFormatted, setDonationAmountFormatted] = useState("");
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentProvider, setPaymentProvider] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [emailPreference, setEmailPreference] = useState('provide');
    const [loading, setLoading] = useState(false);
    const [showEmail, setShowEmail] = useState(true);
    const [isPayPalFormValid, setIsPayPalFormValid] = useState(false);
    // State for currency selection - remains user-selectable
    const [currency, setCurrency] = useState('UGX'); // Default to UGX
    const [paypalClientId, setPaypalClientId] = useState(null);
    const [paypalLoading, setPaypalLoading] = useState(true);
    const [donationType, setDonationType] = useState('one-time');
    const [recurringFrequency, setRecurringFrequency] = useState('');
    const navigate = useNavigate();

    // Fetch PayPal client ID from backend
    useEffect(() => {
        const fetchPaypalClientId = async () => {
            try {
                const response = await fetch(`${baseURL}/paypal/config`);
                const data = await response.json();
                
                if (response.ok && data.paypalClientId) {
                    setPaypalClientId(data.paypalClientId);
                } else {
                    console.error('Failed to fetch PayPal client ID:', data.message);
                    toast.error('Failed to initialize PayPal. Please try another payment method.');
                }
            } catch (error) {
                console.error('Error fetching PayPal client ID:', error);
                toast.error('Error initializing PayPal. Please try another payment method.');
            } finally {
                setPaypalLoading(false);
            }
        };

        if (open && paymentMethod === 'PayPal') {
            fetchPaypalClientId();
        }
    }, [open, paymentMethod]);

    const formatDonationAmount = (value) => {
        if (!value) return "";
        const cleanValue = value.replace(/\D/g, "");
        // Use a generic locale or currency-specific formatting if needed, but for now, keep it simple
        return Number(cleanValue).toLocaleString("en-US"); 
    };

    const handleDonationChange = (e) => {
        const raw = e.target.value;
        const cleanValue = raw.replace(/\D/g, "");
        setDonationAmount(cleanValue);
        setDonationAmountFormatted(formatDonationAmount(cleanValue));
    };

    const validatePhoneNumber = () => {
        // Validation logic for Mobile Money remains the same
        if (paymentMethod !== 'Mobile Money') return true; 

        if (!paymentProvider) {
            toast.error("Please select a Mobile Money provider.");
            return false;
        }

        let raw = phoneNumber.trim();
        if (raw.startsWith('0')) {
            raw = '256' + raw.slice(1);
        }
        const validFormat = /^256\d{9}$/.test(raw);
        if (!validFormat) {
            toast.error("Please enter a valid phone number (e.g., 07XXXXXXXX or 2567XXXXXXXX).");
            return false;
        }
        const providerPrefixes = {
            MTN: ['25676', '25677', '25678', '25679'],
            Airtel: ['25670', '25674', '25675'],
        };
        const validPrefixes = providerPrefixes[paymentProvider];
        const isPrefixMatch = validPrefixes && validPrefixes.some(prefix => raw.startsWith(prefix));
        if (!isPrefixMatch) {
            toast.error(`The number does not match the selected provider (${paymentProvider}).`);
            return false;
        }
        setPhoneNumber(raw);
        return true;
    };

    useEffect(() => {
        const isNameValid = name.trim() !== '';
        const isAmountValid = donationAmount && parseFloat(donationAmount) > 0;
        const isEmailValid = emailPreference === 'anonymous' || validateEmail(email);
        // PayPal validation is now only concerned with having valid form data, 
        // as conversion is handled in `createPayPalOrder`.
        setIsPayPalFormValid(isNameValid && isAmountValid && isEmailValid && paymentMethod === 'PayPal');
    }, [name, donationAmount, email, emailPreference, paymentMethod]);

    const toSentenceCase = (str) => {
        return str
            .toLowerCase()
            .split(' ')
            .filter(word => word.trim() !== '')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    const isNonNegative = (value) => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0;
    };

    // Helper for common donation pre-checks
    const preValidateDonation = () => {
        if (!name) {
            toast.error("Please enter your full name.");
            return false;
        }
        if (!donationAmount || isNaN(parseFloat(donationAmount)) || parseFloat(donationAmount) <= 0) {
            toast.error("Please enter a valid donation amount.");
            return false;
        }
        if (emailPreference === 'provide' && !validateEmail(email)) {
            toast.error("Please provide a valid email address.");
            return false;
        }
        if (donationType === 'recurring' && !recurringFrequency) {
            toast.error("Please select a recurring frequency.");
            return false;
        }
        return true;
    };

    const handleDonate = async () => {
        if (!preValidateDonation()) return;
        if (paymentMethod !== 'Mobile Money') {
            toast.error("Invalid payment method for this handler.");
            return;
        }
        if (!validatePhoneNumber()) return;

        setLoading(true);
        let finalAmount = parseFloat(donationAmount);
        let finalCurrency = currency;
        
        try {
            // Mobile Money: MUST be in UGX. Convert from USD if needed.
            if (currency === 'USD') {
                toast.info("Converting USD to UGX for Mobile Money payment...");
                finalAmount = await convertCurrency(finalAmount, 'USD', 'UGX');
                finalCurrency = 'UGX';
            }

            const donationData = {
                donationAmount: finalAmount,
                currency: finalCurrency,
                paymentMethod,
                phoneNumber,
                paymentProvider,
                email: emailPreference === 'provide' ? email : undefined,
                name: toSentenceCase(name),
                showEmail,
                campaignId: campaign.id,
                campaignName: campaign.name,
                is_recurring: donationType === 'recurring',
                frequency: donationType === 'recurring' ? recurringFrequency : undefined,
            };

            await new Promise(resolve => setTimeout(resolve, 4000)); // Simulating network latency
            const response = await fetch(`${baseURL}/data/backend/donate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(donationData),
            });
            const data = await response.json();
            
            if (response.ok) {
                toast.success(data.message || `Donation of ${finalCurrency} ${finalAmount.toLocaleString()} successful! Thank you.`);
                onClose();
                // Reset form fields
                setDonationAmount('');
                setDonationAmountFormatted('');
                setPaymentMethod('');
                setPhoneNumber('');
                setName('');
                setEmail('');
                setEmailPreference('provide');
                setDonationType('one-time');
                setRecurringFrequency('');
                setTimeout(() => { navigate("/campaigns"); }, 100);
            } else {
                toast.error(data.message || 'Donation failed. Please try again.');
            }
        } catch (error) {
            toast.error(`An error occurred: ${error.message || "Failed to process donation."}`);
            console.error("Donation submission error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePaystackInitiate = async () => {
        if (!preValidateDonation()) return;
        if (paymentMethod !== 'Paystack') {
            toast.error("Invalid payment method for this handler.");
            return;
        }
        // Paystack needs an email to work
        if (!email) {
            toast.error("Paystack requires an email address.");
            return;
        }
        
        setLoading(true);
        let finalAmount = parseFloat(donationAmount);
        let finalCurrency = currency;

        try {
            // Paystack: MUST be in USD. Convert from UGX if needed.
            if (currency === 'UGX') {
                toast.info("Converting UGX to USD for Paystack payment...");
                finalAmount = await convertCurrency(finalAmount, 'UGX', 'USD');
                finalCurrency = 'USD';
            }
            
            const response = await fetch(`${baseURL}/data/backend/paystack/initiate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    campaignId: campaign.id,
                    campaignName: campaign.name,
                    name: toSentenceCase(name),
                    email,
                    amount: finalAmount,
                    currency: finalCurrency,
                    is_recurring: donationType === 'recurring',
                    frequency: donationType === 'recurring' ? recurringFrequency : undefined,
                }),
            });

            const data = await response.json();

            if (response.ok && data.authorization_url) {
                toast.success('You are being redirected to Paystack...');
                setTimeout(() => {
                    window.location.href = data.authorization_url;
                    onClose();
                    // Reset form fields
                    setDonationAmount('');
                    setDonationAmountFormatted('');
                    setPaymentMethod('');
                    setName('');
                    setEmail('');
                    setEmailPreference('provide');
                    setDonationType('one-time');
                    setRecurringFrequency('');
                    setLoading(false);
                }, 100);
            } else {
                toast.error(data.message || 'Failed to initiate Paystack payment.');
                setLoading(false);
            }
        } catch (error) {
            toast.error(`An error occurred: ${error.message || "Failed to initiate Paystack payment."}`);
            setLoading(false);
        }
    };

    const handlePaymentMethodChange = (e) => {
        const newMethod = e.target.value;
        setPaymentMethod(newMethod);
        setPaymentProvider('');
        setPhoneNumber('');
        // NOTE: Currency is intentionally *not* auto-set here, allowing the user to choose.
    };
    
    const handleCurrencyChange = (e) => {
        const newCurrency = e.target.value;
        setCurrency(newCurrency);
        // Clear donation amount to reset formatting and avoid confusion
        setDonationAmount('');
        setDonationAmountFormatted('');
    };

    const handleEmailPreferenceChange = (e) => {
        const preference = e.target.value;
        setEmailPreference(preference);
        setShowEmail(preference === 'provide');
        if (preference === 'anonymous') {
            setEmail('');
        }
    };

    const handleFinalDonation = () => {
        switch (paymentMethod) {
            case 'Mobile Money':
                handleDonate();
                break;
            case 'Paystack':
                handlePaystackInitiate();
                break;
            case 'PayPal':
                // PayPal buttons handle their own flow
                if (isPayPalFormValid) {
                    toast.info("Click the PayPal button below to proceed with your payment.");
                } else {
                    preValidateDonation(); // Show specific errors
                }
                break;
            default:
                toast.error("Please select a payment method.");
        }
    };

    const createPayPalOrder = async (data, actions) => {
        if (!preValidateDonation()) return null; // Use pre-validation here
        
        let finalAmount = parseFloat(donationAmount);
        
        try {
            // PayPal: MUST be in USD. Convert from UGX if needed.
            if (currency === 'UGX') {
                toast.info("Converting UGX to USD for PayPal payment...");
                finalAmount = await convertCurrency(finalAmount, 'UGX', 'USD');
                // The finalAmount is now in USD, but the original currency is passed for logging/tracking
            }

            const response = await fetch(`${baseURL}/paypal/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: finalAmount, // Converted or original USD amount
                    currency: 'USD', // PayPal only accepts USD for this flow
                    name: toSentenceCase(name),
                    email,
                    campaignId: campaign.id,
                    campaignName: campaign.name,
                    is_recurring: donationType === 'recurring',
                    frequency: donationType === 'recurring' ? recurringFrequency : undefined,
                }),
            });
            const order = await response.json();
            if (!response.ok) throw new Error(order.message || 'Failed to create PayPal order.');
            return order.orderID;
        } catch (error) {
            console.error("Failed to create PayPal order:", error);
            toast.error(`Failed to create PayPal order: ${error.message}`);
            return null;
        }
    };

    const onPayPalApprove = async (data, actions) => {
        try {
            const response = await fetch(`${baseURL}/paypal/capture-order/${data.orderID}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const captureData = await response.json();

            if (captureData.status === 'COMPLETED') {
                toast.success(captureData.message || "Donation processed successfully. Thank you!");
                onClose();
                setDonationAmount('');
                setDonationAmountFormatted('');
                setEmail('');
                setName('');
                setPaymentMethod('');
                setDonationType('one-time');
                setRecurringFrequency('');
                navigate("/campaigns");
            } else if (captureData.status === 'PROCESSING') {
                toast.info(captureData.message || "Your payment is being processed. You'll receive a confirmation email.");
                onClose();
                setDonationAmount('');
                setDonationAmountFormatted('');
                setEmail('');
                setName('');
                setPaymentMethod('');
                setDonationType('one-time');
                setRecurringFrequency('');
            } else {
                toast.error(captureData.message || 'Payment capture failed. Please try again.');
            }
        } catch (error) {
            console.error("PayPal capture error:", error);
            toast.error("An error occurred during payment capture. Please contact support.");
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                borderRadius: '16px',
                '& .MuiDialog-paper': {
                    borderRadius: '16px',
                },
            }}
        >
            <DialogTitle sx={{ textAlign: 'left', color: COLORS.primary, fontWeight: 'bold', padding: 2, fontSize: '1.6rem' }}>
                Donate to {campaign.name}
            </DialogTitle>
            <DialogContent sx={{ padding: 3 }}>
                <Typography variant="body1" color={COLORS.text} sx={{ mb: 4 }}>
                    Your contribution supports this cause. <strong>Currency conversion will be handled automatically</strong> if you select a payment method that requires a different currency.
                </Typography>

                {!!campaign?.accepts_recurring && (
                    <Tabs
                        value={donationType}
                        onChange={(_, v) => { setDonationType(v); setRecurringFrequency(''); }}
                        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label="One-Time" value="one-time" />
                        <Tab label="Recurring" value="recurring" />
                    </Tabs>
                )}

                <TextField
                    label="Enter Names"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={name}
                    required
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 3, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    InputLabelProps={{ style: { color: COLORS.primary } }}
                    InputProps={{
                        style: { color: COLORS.text },
                        startAdornment: (
                            <InputAdornment position="start">
                                <FontAwesomeIcon icon={faUser} style={{ color: COLORS.primary, fontSize: '1.2em' }} />
                            </InputAdornment>
                        ),
                    }}
                    placeholder="Enter Name"
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    {/* Currency Dropdown */}
                    <FormControl variant="outlined" sx={{ minWidth: 120, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                        <InputLabel id="currency-select-label" style={{ color: COLORS.primary }}>Currency</InputLabel>
                        <Select
                            labelId="currency-select-label"
                            id="currency-select"
                            value={currency}
                            onChange={handleCurrencyChange}
                            label="Currency"
                            sx={{ borderRadius: '10px' }}
                        >
                            <MenuItem value="UGX">UGX (Shilling)</MenuItem>
                            <MenuItem value="USD">USD (Dollar)</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Donation Amount Field */}
                    <TextField
                        label={`Donation Amount (${currency})`}
                        fullWidth
                        variant="outlined"
                        required
                        value={donationAmountFormatted}
                        onChange={handleDonationChange}
                        sx={{
                            flexGrow: 1,
                            borderRadius: "10px",
                            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        }}
                        InputLabelProps={{ style: { color: COLORS.primary } }}
                        InputProps={{
                            style: { color: COLORS.text },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon
                                        icon={currency === 'USD' ? faDollarSign : faMoneyBillWave}
                                        style={{ color: COLORS.primary, fontSize: "1.2em" }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter donation amount"
                    />
                </Box>

                {donationType === 'recurring' && (
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth variant="outlined" sx={{ borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                            <InputLabel id="recurring-frequency-label" style={{ color: COLORS.primary }}>Frequency</InputLabel>
                            <Select
                                labelId="recurring-frequency-label"
                                value={recurringFrequency}
                                onChange={(e) => setRecurringFrequency(e.target.value)}
                                label="Frequency"
                                sx={{ borderRadius: '10px' }}
                            >
                                {(() => {
                                    const rf = campaign?.recurring_frequencies;
                                    try {
                                        return (Array.isArray(rf) ? rf : (typeof rf === 'string' ? JSON.parse(rf) : [])) || [];
                                    } catch { return []; }
                                })().map((freq) => (
                                    <MenuItem key={freq} value={freq} sx={{ textTransform: 'capitalize' }}>
                                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {paymentMethod && paymentMethod !== 'Paystack' && (
                            <Alert severity="info" sx={{ mt: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>
                                Recurring {paymentMethod} donations are manually scheduled. You will receive a reminder for each {recurringFrequency || 'scheduled'} payment.
                            </Alert>
                        )}
                    </Box>
                )}

                {emailPreference === 'provide' && (
                    <TextField
                        label="Email Address"
                        type="email"
                        fullWidth
                        required
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        InputLabelProps={{ style: { color: COLORS.primary } }}
                        InputProps={{
                            style: { color: COLORS.text },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faEnvelope} style={{ color: COLORS.primary, fontSize: '1.2em' }} />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your email"
                    />
                )}

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <FormLabel component="legend" sx={{ color: COLORS.text }}>Email Visibility</FormLabel>
                    <RadioGroup
                        value={emailPreference}
                        onChange={handleEmailPreferenceChange}
                        row
                    >
                        <FormControlLabel value="provide" control={<Radio />} label="Share my email with the fundraiser" />
                        <FormControlLabel value="anonymous" control={<Radio />} label="Keep my email private" />
                    </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <FormLabel component="legend" sx={{ color: COLORS.text }}>
                        Choose Donation Method
                    </FormLabel>
                    <RadioGroup
                        value={paymentMethod}
                        onChange={handlePaymentMethodChange}
                    >
                        <FormControlLabel
                            value="Mobile Money"
                            control={<Radio />}
                            label="Mobile Money (UGX Preferred)"
                        />
                        <FormControlLabel
                            value="PayPal"
                            control={<Radio />}
                            label="PayPal (USD Preferred)"
                        />
                        <FormControlLabel
                            value="Paystack"
                            control={<Radio />}
                            label="Paystack (USD Preferred)"
                        />
                    </RadioGroup>
                </FormControl>

                {paymentMethod === 'Mobile Money' && (
                    <>
                        {currency === 'USD' && (
                            <Typography variant="body2" color="warning.main" sx={{ mb: 3, textAlign: 'center' }}>
                                Your donation of <strong>USD</strong> will be converted to <strong>UGX</strong> for Mobile Money payment.
                            </Typography>
                        )}
                        <FormControl fullWidth variant="outlined" sx={{ mb: 2, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                            <InputLabel>Payment Provider</InputLabel>
                            <Select
                                value={paymentProvider}
                                onChange={(e) => setPaymentProvider(e.target.value)}
                                label="Payment Provider"
                                sx={{ borderRadius: '10px' }}
                            >
                                <MenuItem value="Airtel">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={AirtelLogo} alt="Airtel" width={30} height={30} style={{ marginRight: 10 }} />
                                        Airtel Money
                                    </Box>
                                </MenuItem>
                                <MenuItem value="MTN">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={MtnLogo} alt="MTN" width={30} height={30} style={{ marginRight: 10 }} />
                                        MTN Mobile Money
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Phone Number"
                            type="tel"
                            fullWidth
                            variant="outlined"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            sx={{ mb: 3, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            InputLabelProps={{ style: { color: COLORS.primary } }}
                            InputProps={{
                                style: { color: COLORS.text },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FontAwesomeIcon icon={faPhone} style={{ color: COLORS.primary, fontSize: '1.2em' }} />
                                    </InputAdornment>
                                ),
                            }}
                            placeholder="e.g., 07XXXXXXXX or 2567XXXXXXXX"
                        />
                    </>
                )}
                
                {(paymentMethod === 'Paystack' || paymentMethod === 'PayPal') && currency === 'UGX' && (
                    <Typography variant="body2" color="warning.main" sx={{ mb: 1, textAlign: 'center' }}>
                        Your donation of <strong>UGX</strong> will be converted to <strong>USD</strong> for {paymentMethod} payment.
                    </Typography>
                )}

            </DialogContent>
            <DialogActions
                sx={{
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column', 
                    gap: 1,
                }}
            >
                {paymentMethod === 'PayPal' ? (
                    <Box sx={{ width: '100%' }}>
                        <PayPalButtonWrapper
                            paypalLoading={paypalLoading}
                            paypalClientId={paypalClientId}
                            isPayPalFormValid={isPayPalFormValid}
                            createPayPalOrder={createPayPalOrder}
                            onPayPalApprove={onPayPalApprove}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row', 
                            gap: 1, 
                        }}
                    >
                        <Button
                            onClick={onClose}
                            variant='outlined'
                            sx={{
                                flex: 1, 
                                backgroundColor: COLORS.white,
                                color: COLORS.primary,
                                borderRadius: '8px',
                                px: 3,
                                py: 1.6,
                                boxShadow: 0,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: 0,
                                    backgroundColor: COLORS.primary,
                                    color: COLORS.white,
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleFinalDonation}
                            variant="contained"
                            sx={{
                                flex: 1, 
                                backgroundColor: COLORS.accent,
                                color: COLORS.white,
                                borderRadius: '8px',
                                px: 3,
                                py: 2,
                                boxShadow: 0,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                position: 'relative',
                                minHeight: '50px',
                                '&:hover': {
                                    boxShadow: 0,
                                    backgroundColor: COLORS.accentDark,
                                },
                            }}
                            disabled={loading || !paymentMethod || !donationAmount || parseFloat(donationAmount) <= 0}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    visibility: loading ? 'hidden' : 'visible',
                                }}
                            >
                                {loading ? '' : 'Donate'}
                            </Typography>
                            {loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        inset: 0,
                                    }}
                                >
                                    <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'white',
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Processing
                                    </Typography>
                                </Box>
                            )}
                        </Button>
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DonateDialog;